/**
 * src/utils/mutation-observer.ts
 *
 * Observa o DOM em busca de novos elementos com o atributo
 * `[data-cenario-map]`.
 *
 * Esse mecanismo é essencial para compatibilidade com:
 * - carregamento dinâmico via AJAX,
 * - comportamento padrão do Moodle,
 * - conteúdos injetados após o load inicial.
 */

import { CenarioEngine } from '../ui/cenario';

// WeakSet garante que elementos já processados
// não sejam inicializados novamente
const initializedElements = new WeakSet<HTMLElement>();

/**
 * Verifica se o elemento está dentro de um editor de texto do Moodle
 * (TinyMCE, Atto) para evitar que a engine renderize durante a edição.
 */
function isInMoodleEditor(element: HTMLElement): boolean {
  // 1. Verifica ancestrais com contenteditable (padrão HTML5)
  if (element.closest('[contenteditable="true"]')) {
    return true;
  }

  // 2. Verifica classes específicas do corpo do editor (TinyMCE/Atto em iFrame)
  const body = document.body;
  if (
    body.id === 'tinymce' ||
    body.classList.contains('editor_atto_content') ||
    body.classList.contains('mce-content-body')
  ) {
    return true;
  }

  return false;
}

/**
 * Inicializa um elemento gatilho, se ainda não processado.
 *
 * @param element Elemento com atributo `data-cenario-map`
 */
function initElement(element: HTMLElement) {
  if (initializedElements.has(element)) return;

  // VERIFICAÇÃO DE SEGURANÇA:
  // Se estiver editando no Moodle, não inicializa a engine.
  if (isInMoodleEditor(element)) {
    console.warn('[CenarioMap] Modo editor detectado. Renderização pausada.');
    return;
  }

  // Marca como iniciado para evitar loops
  initializedElements.add(element);

  console.warn('[CenarioMap] Gatilho detectado:', element.dataset.cenarioMap);

  const engine = new CenarioEngine(element);

  engine.init().catch((err) => {
    console.error('[CenarioMap] Erro ao inicializar engine:', err);
  });
}

/**
 * Inicia o MutationObserver global.
 *
 * Fluxo:
 * 1. Inicializa elementos já existentes no DOM.
 * 2. Observa inserções futuras (AJAX / render dinâmico).
 * 3. Detecta tanto o nó raiz quanto filhos inseridos.
 */
export function startObserver() {
  // Inicialização imediata (conteúdo já presente)
  const existingElements = document.querySelectorAll<HTMLElement>('[data-cenario-map]');
  existingElements.forEach(initElement);

  // Observador para alterações futuras no DOM
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        const el = node as HTMLElement;

        // Caso o próprio nó seja um gatilho
        if (el.hasAttribute('data-cenario-map')) {
          initElement(el);
        }

        // Caso existam gatilhos dentro do nó adicionado
        el.querySelectorAll<HTMLElement>('[data-cenario-map]').forEach(initElement);
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  console.warn('[CenarioMap] MutationObserver iniciado.');
}
